import useTransactionsApi from '../api/useTransactionsApi';
import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import { type StoreLoadOptions } from '@/types';
import { useTransactionsStore } from '#imports';

export const useRelatedTransactions = () => {
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;
  const route = useRoute();
  const { getInOutTransactionDetails, getInOutRelatedTransactions, getP2PRelatedTransactions } =
    useTransactionsApi();
  const { convertToSearchParams } = useUtils();
  const { notifyApiError } = useApiErrorSnackbar();
  const {
    transactionInfos,
    transactionP2PInfos,
    relatedTransactions,
    parentTransaction,
    relatedTransactionsCount
  } = storeToRefs(useTransactionsStore());

  const transactionId = computed(() =>
    Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
  );

  const loadRelatedTransactions = async (isP2P?: boolean) => {
    if (transactionInfos.value) {
      try {
        const queryObject: StoreLoadOptions = {
          skip: 0,
          take: 10,
          requireTotalCount: false,
          paginateViaPrimaryKey: true,
          sort: JSON.stringify([{ selector: 'date', desc: true }])
        };

        const searchParams = convertToSearchParams(queryObject);
        if (transactionInfos.value.parentTransactionId) {
          const transactionInOutData = await getInOutTransactionDetails(
            transactionInfos.value.parentTransactionId.toString()
          );
          parentTransaction.value = transactionInOutData.MoneyTransaction;
        } else {
          parentTransaction.value = transactionInfos.value;
        }
        if (parentTransaction.value) {
          const data = await getInOutRelatedTransactions(
            parentTransaction.value?.id.toString(),
            searchParams.toString()
          );
          relatedTransactions.value = data;
        }
        if (!isP2P) {
          const relatedTransactions = await getInOutRelatedTransactions(
            transactionInfos.value?.id.toString(),
            searchParams.toString()
          );
          relatedTransactionsCount.value = relatedTransactions.length;
        }
      } catch (error: unknown) {
        notifyApiError(error, { title: t('common.transactionDetailsNotFound') });
      }
    }
  };

  const loadP2PRelatedTransactions = async () => {
    if (transactionP2PInfos.value) {
      try {
        const data = await getP2PRelatedTransactions(transactionId.value);
        transactionInfos.value = data;
        relatedTransactionsCount.value = [data].length;
        await loadRelatedTransactions(true);
      } catch (error: unknown) {
        const errorMessage = (error as { message: string }).message;
        const parsedErrorObject = JSON.parse(errorMessage);
        if (parsedErrorObject?.Code === 5) {
          parentTransaction.value = {
            id: transactionP2PInfos.value.id,
            type: 2,
            status: transactionP2PInfos.value.status,
            paymentMethod: transactionP2PInfos.value.paymentMethod,
            settlementDate: transactionP2PInfos.value.SettlementDate,
            category: transactionP2PInfos.value.category,
            date: transactionP2PInfos.value.date,
            amount: transactionP2PInfos.value.amount
          };
        } else {
          notifyApiError(error, { title: t('common.transactionDetailsNotFound') });
        }
      }
    }
  };

  return {
    loadRelatedTransactions,
    loadP2PRelatedTransactions
  };
};
